import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { Input, Form, Upload } from "antd";
import { IoIosSearch } from "react-icons/io";
import { FilterMatchMode } from "primereact/api";
import { UploadOutlined } from "@ant-design/icons";
import { Toast } from "primereact/toast";
import axiosInstance from "../utils/axios";
import { usePageSummary } from "../hooks/usePage";
import { useFormik } from "formik";
import * as yup from "yup";
import TinyMCEEditor from "../components/QuillEditor";
import { Sidebar } from "primereact/sidebar";
import { useBoardMember } from "../hooks/useBoard";
import { truncateText } from "../utils/authHelpers";
import { renderFieldLabel } from "../utils/formHelpers";
import { HideSortIcons } from "../utils/tableStyles";
const ManageBoardMembers = () => {
  const {
    updateBoardPage,
    updateBoardPageloading,
    boardSummary,
    updateBordPageSuccess,
  } = usePageSummary();
  const {
    boardMembers,
    isLoading,
    createBoard,
    createBoardLoading,
    createBoardSuccess,
    deleteBoard,
    updateBoard,
    deleteBoardLoading,
    updateBoardLoading,
    updateBoardSuccess,
    deleteBoardSuccess,
    error,
  } = useBoardMember();
  const [visibleRight, setVisibleRight] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [selectedMember, setSelectedMember] = useState({
    fullName: "",
    email: "",
    phone: "",
    bio: "",
    company: "",
  });
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [visible, setVisible] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const createBoardFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: selectedMember.fullName ?? "",
      email: selectedMember.email ?? "",
      phone: selectedMember.phone ?? "",
      bio: selectedMember.bio ?? "",
      company: selectedMember.company ?? "",
      image: null,
    },
    validationSchema: yup.object({
      fullName: yup.string().required("Full name is required"),
      email: yup.string().email("invalid email").required("Email is required"),
      phone: yup.string().required("phone number is required"),
      bio: yup.string().required("Member bio is required"),
      company: yup.string().required("Company name is required"),
    }),
    onSubmit: (values) => {
      if (editingId != null) {
        updateBoard({
          id: editingId,
          boardMemberData: {
            ...values,
            image: values.image,
          },
        });
      } else
        createBoard({
          boardMemberData: {
            ...values,
            image: values.image,
          },
        });
    },
  });

  const addSummaryFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      summaryText: boardSummary?.description || "",
    },
    validationSchema: yup.object({
      summaryText: yup.string().required("summary is required"),
    }),
    onSubmit: (values) => {
      updateBoardPage({ description: values.summaryText });
    },
  });

  useEffect(() => {
    if (updateBordPageSuccess) {
      addSummaryFormik.setFieldValue(
        "summaryText",
        addSummaryFormik.values.summaryText
      );
    }
  }, [updateBordPageSuccess]);

  const handleFileUpload = (info) => {
    const file = info.fileList[0]?.originFileObj || null;
    setFileList(info.fileList);
    createBoardFormik.setFieldValue("image", file);
  };

  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    let _filters = { ...filters };
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => (
    <div className="flex items-end justify-end">
      <Input
        placeholder="Search"
        className="text-sm focus:ring-0 focus:outline-none"
        value={globalFilterValue}
        onChange={onGlobalFilterChange}
        prefix={<IoIosSearch />}
      />
    </div>
  );

  const customHeader = () => (
    <div className="flex items-start justify-start">
      <h1 className="font-semibold">Add Summary on Board page</h1>
    </div>
  );

  const deleteMember = (id) => {
    setDeletingId(id);
    deleteBoard(id);
  };

  const handleClose = () => {
    setVisible(false);
    setSelectedMember({
      fullName: "",
      email: "",
      phone: "",
      bio: "",
      company: "",
    });
    createBoardFormik.resetForm();
  };

  useEffect(() => {
    if (createBoardSuccess || updateBoardSuccess || deleteBoardSuccess) {
      handleClose();
    }
  }, [createBoardSuccess, updateBoardSuccess, deleteBoardSuccess]);

  if (isLoading)
    return (
      <div className="flex h-screen justify-center items-center">
        <i
          className="pi pi-spin pi-spinner text-red-700"
          style={{ fontSize: "2rem" }}
        ></i>
      </div>
    );
  if (error) return <p className="text-center text-red-500">{error}</p>;

  return (
    <div className="p-5">
      <HideSortIcons />
      <div className="flex justify-between">
        <h1 className="lg:text-2xl font-semibold text-[#003049] mb-5">
          Board Members Management
        </h1>
        <div className="flex gap-5 items-center">
          <div>
            <Button
              label="Add Member"
              icon="pi pi-plus"
              className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
              onClick={() => setVisible(true)}
            />
          </div>
          <div>
            <Button
              label="Add Summary"
              icon="pi pi-plus"
              className="bg-[#BA1500] text-white btn text-[10px] lg:text-sm focus:outline-none focus:ring-0"
              onClick={() => setVisibleRight(true)}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-md mt-5 text-[18px]">
        <div className="card">
          <DataTable
            header={renderHeader}
            globalFilter={filters.global.value}
            emptytext="No members found."
            filters={filters}
            value={boardMembers}
            paginator
            rows={5}
            sortMode="single"
            showSortIcon={false}
            tableStyle={{ minWidth: "50rem", fontSize: "14px" }}
            className="text-xs sm:text-sm md:text-base"
            onRowDoubleClick={(e) => {
              setEditingId(e.data.id);
              setSelectedMember({
                fullName: e.data.fullName,
                email: e.data.email,
                phone: e.data.phone,
                bio: e.data.bio,
                company: e.data.company,
              });
              setVisible(true);
            }}
          >
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <Button
                    icon="pi pi-pencil"
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => {
                      setEditingId(rowData.id);
                      setSelectedMember({
                        fullName: rowData.fullName,
                        email: rowData.email,
                        phone: rowData.phone,
                        bio: rowData.bio,
                        company: rowData.company,
                      });
                      setVisible(true);
                    }}
                  />
                </div>
              )}
            />
            <Column
              field="imageUrl"
              header="Image"
              style={{ width: "25%" }}
              body={(rowData) => (
                <img
                  src={rowData.imageUrl}
                  alt={rowData.fullName}
                  className="w-16 h-16 rounded-full"
                />
              )}
              headerClassName="bg-[#003049] text-white"
            />
            <Column
              field="fullName"
              header="Name"
              headerClassName="bg-[#003049] text-white"
              className="max-w-[120px] truncate"
              body={(rowData) => (
                <span title={rowData.fullName}>
                  {truncateText(rowData.fullName, 20)}
                </span>
              )}
              sortable
            />
            <Column
              field="email"
              header="Email"
              headerClassName="bg-[#003049] text-white"
              className="max-w-[140px] truncate"
              body={(rowData) => (
                <span title={rowData.email}>
                  {truncateText(rowData.email, 25)}
                </span>
              )}
              sortable
            />
            <Column
              field="phone"
              header="Phone"
              headerClassName="bg-[#003049] text-white"
              className="max-w-[100px] truncate"
              style={{ width: "25%" }}
              body={(rowData) => (
                <span title={rowData.phone}>
                  {truncateText(rowData.phone, 15)}
                </span>
              )}
              sortable
            />
            <Column
              field="company"
              header="Company"
              headerClassName="bg-[#003049] text-white"
              className="max-w-[120px] truncate"
              style={{ width: "25%" }}
              body={(rowData) => (
                <span title={rowData.company}>
                  {truncateText(rowData.company, 20)}
                </span>
              )}
              sortable
            />
            <Column
              field="bio"
              header="Bio"
              headerClassName="bg-[#003049] text-white"
              className="max-w-[160px] truncate"
              style={{ width: "25%" }}
              body={(rowData) => (
                <span title={rowData.bio}>{truncateText(rowData.bio, 30)}</span>
              )}
              sortable
            />
            <Column
              header="Action"
              headerClassName="bg-[#003049] text-white"
              style={{ width: "10%" }}
              body={(rowData) => (
                <div className="flex gap-5">
                  <Button
                    icon={
                      deletingId === rowData.id
                        ? "pi pi-spin pi-spinner"
                        : "pi pi-trash"
                    }
                    className="text-red-600 hover:text-red-800"
                    onClick={() => deleteMember(rowData.id)}
                  />
                </div>
              )}
            />
          </DataTable>
        </div>
      </div>

      <Sidebar
        visible={visibleRight}
        position="right"
        onHide={() => setVisibleRight(false)}
        style={{ width: "30rem" }}
        header={customHeader}
      >
        <div className="mt-5">
          <form onSubmit={addSummaryFormik.handleSubmit}>
            <div className="py-2 my-2">
              <TinyMCEEditor
                value={addSummaryFormik.values.summaryText}
                onChange={(html) =>
                  addSummaryFormik.setFieldValue("summaryText", html)
                }
                onBlur={() =>
                  addSummaryFormik.setFieldTouched("summaryText", true)
                }
              />

              {addSummaryFormik.touched.summaryText &&
                addSummaryFormik.errors.summaryText && (
                  <div className="text-[#BA1500] text-sm" aria-live="polite">
                    {addSummaryFormik.errors.summaryText}
                  </div>
                )}
            </div>
            <button
              type="submit"
              className="w-full bg-[#BA1500] text-center text-white py-3 disabled:opacity-50"
              disabled={updateBoardPageloading}
            >
              {updateBoardPageloading ? (
                <i
                  className="pi pi-spin pi-spinner text-white"
                  style={{ fontSize: "2rem" }}
                ></i>
              ) : (
                "save"
              )}
            </button>
          </form>
        </div>
      </Sidebar>

      <Dialog
        header={editingId ? "Edit Member" : "Add Member"}
        visible={visible}
        className="w-full lg:w-[50%]"
        onHide={handleClose}
      >
        <Form layout="vertical" onFinish={createBoardFormik.handleSubmit}>
          {["fullName", "email", "phone", "company", "bio"].map((field) => (
            <Form.Item
              label={
                <span>
                  {field.charAt(0).toUpperCase() + field.slice(1)}{" "}
                  <span className="text-red-500 ml-1">*</span>
                </span>
              }
              key={field}
              validateStatus={
                createBoardFormik.errors[field] &&
                createBoardFormik.touched[field]
                  ? "error"
                  : ""
              }
              help={
                createBoardFormik.touched[field] &&
                createBoardFormik.errors[field]
              }
            >
              <Input
                name={field}
                value={createBoardFormik.values[field]}
                onChange={createBoardFormik.handleChange}
                onBlur={createBoardFormik.handleBlur}
                className="rounded-md"
              />
            </Form.Item>
          ))}

          <Form.Item label="Upload image">
            <Upload
              fileList={fileList}
              beforeUpload={() => false}
              onChange={handleFileUpload}
              maxCount={1}
              accept="image/*"
            >
              <Button type="button" icon={<UploadOutlined />}>
                Select Image
              </Button>
            </Upload>
          </Form.Item>

          <button
            type="submit"
            className="btn w-full bg-[#BA1500] text-white py-3"
          >
            <i
              className={`${
                createBoardLoading || updateBoardLoading
                  ? "pi pi-spin pi-spinner"
                  : "pi pi-save"
              } mr-2`}
            ></i>
            {editingId != null
              ? "update"
              : createBoardLoading
              ? "Saving..."
              : updateBoardLoading
              ? "updating..."
              : "Save"}
          </button>
        </Form>
      </Dialog>
    </div>
  );
};

export default ManageBoardMembers;
